/** @format */

import { isPlatformBrowser, Location } from '@angular/common';

import {
  Injectable,
  PLATFORM_ID,
  DOCUMENT,
  inject,
  makeStateKey,
  TransferState,
  signal,
  computed,
  Signal,
} from '@angular/core';
import { Chain, KillSwitches, Environment, KillSwitch } from '@shared/data-access/interfaces';
import { injectServerRequestKillSwitches } from '@shared/data-access/config';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class KillswitchService {
  private readonly killSwitchData = injectServerRequestKillSwitches();
  private readonly state = inject(TransferState);
  private readonly environment = inject(Environment);
  private readonly chain = inject(Chain);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly KILLSWITCH_STATE_ENVIRONMENT_KEY_PREFIX = 'kill-switches-environment' as const;

  //# region *** Data For Overrides ***

  // Need to parse params from Location since the service can be used outside a routing context (e.g. app component)
  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.location.path()),
      startWith(this.location.path()),
    ),
  );

  private readonly flagParams = computed(() => {
    // Placeholder domain doesn't matter, it's just needed for using `URL()`
    const parsedUrl = new URL(`https://placeholder.com${this.currentPath()}`);
    return Object.fromEntries(
      [...parsedUrl.searchParams]
        .filter(([key]) => key.startsWith('flag_'))
        .map(([key, value]) => [key.substring(5), value]), // strip 'flag_' from key
    );
  });

  private readonly _windowFlagsVersion = signal(0);

  //# endregion

  private readonly baseKillSwitches = this.getKillSwitchesFromEnvironment();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // Increment a signal whenever WindowFlagsEvent fires so killswitches are re-calculated
      fromEvent(this.document, 'WindowFlagsEvent')
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          this._windowFlagsVersion.update((v) => v + 1);
        });
    }
  }

  /**
   * All the environment killswitches, with overrides applied to the values for the current chain.
   */
  readonly killSwitchesFromEnvironment = computed(
    () => {
      const chain = this.chain.chainIdentifierAbbr;
      const flagParams = this.flagParams();

      this._windowFlagsVersion(); // Recalculate when this updates
      const win = this.document?.defaultView;

      const result: KillSwitches = {};

      for (const [killSwitchName, killSwitchValues] of Object.entries(this.baseKillSwitches)) {
        const overrideName = `${killSwitchName}Override`;
        const override =
          this.baseKillSwitches[overrideName] !== undefined &&
          this.baseKillSwitches[overrideName] !== null
            ? this.baseKillSwitches[overrideName][chain]
            : undefined;

        // Override Priority 1. URL Query Param flag_<ks> takes precedence over all other killswitches
        if (killSwitchName in flagParams) {
          result[killSwitchName] = {
            ...killSwitchValues,
            [chain]: flagParams[killSwitchName] === 'true',
          };
          continue;
        }
        // Override Priority 2. If the killswitch is available in the window object, use the window object value
        if (isPlatformBrowser(this.platformId)) {
          const windowFlag = (win as typeof win & Record<string, unknown>)?.[killSwitchName];
          if (windowFlag !== undefined) {
            result[killSwitchName] = {
              ...killSwitchValues,
              [chain]: !!windowFlag,
            };
            continue;
          }
        }
        // Override Priority 3. If a killswitch with 'Override' appended to the name is present, use the 'Override' value
        if (override !== undefined) {
          result[killSwitchName] = {
            ...killSwitchValues,
            [chain]: override,
          };
          continue;
        }
        // Default. If no other overrides are present, use the main killswitch object value
        if (this.baseKillSwitches[killSwitchName]) {
          result[killSwitchName] = { ...killSwitchValues };
        }
      }

      return result;
    },
    {
      equal: (a, b) => {
        // Tested, faster in Safari than JSON.stringify
        const keysA = Object.keys(a);
        if (keysA.length !== Object.keys(b).length) return false;
        return keysA.every((ks) => {
          const valA = a[ks];
          const valB = b[ks];
          if (valA === valB) return true;
          if (!valA || !valB) return false;
          const switchKeys = Object.keys(valA) as (keyof KillSwitch)[];
          return switchKeys.every((brand) => valA[brand] === valB[brand]);
        });
      },
    },
  );

  private getKillSwitchesFromEnvironment(): KillSwitches {
    // Get unique key
    const STATE_KEY = makeStateKey<KillSwitches | null>(
      `${this.KILLSWITCH_STATE_ENVIRONMENT_KEY_PREFIX}`,
    );

    // Attempt to get the state by key
    const stateData: KillSwitches | null = this.state.get<KillSwitches | null>(STATE_KEY, null);
    // Check to see if state exists
    if (stateData !== null) {
      // Return the existing state
      return stateData;
    }

    const returnData: KillSwitches =
      this.killSwitchData === null
        ? { ...this.environment.killSwitch }
        : { ...this.killSwitchData };

    this.state.set(STATE_KEY, returnData);

    return returnData;
  }

  /**
   * All killswitches for the current chain + environment.
   * Reactively updates when `killSwitchesFromEnvironment` changes.
   */
  readonly killSwitchesForCurrentChain: Signal<Record<string, boolean>> = computed(
    () => {
      const mainKillSwitchObject = this.killSwitchesFromEnvironment();
      const chain = this.chain.chainIdentifierAbbr;

      const result: Record<string, boolean> = {};

      for (const [killSwitchName, killSwitchValues] of Object.entries(mainKillSwitchObject)) {
        result[killSwitchName] = killSwitchValues?.[chain] ?? false;
      }

      return result;
    },
    {
      equal: (a, b) => {
        const keysA = Object.keys(a);
        return keysA.length === Object.keys(b).length && keysA.every((k) => a[k] === b[k]);
      },
    },
  );

  /**
   * The current resolved state of the named killswitch for the active chain + environment, with overrides applied.
   * Reactively updates automatically whenever `killSwitchesForCurrentChain` updates.
   *
   * @param name The killswitch name (e.g. `'availabilityOnProductCardsEnabled'`)
   * @returns A readonly `Signal<boolean>` — `false` if the killswitch is absent or unresolved
   */
  killSwitchForCurrentChain(name: string): Signal<boolean> {
    return computed(() => this.killSwitchesForCurrentChain()[name] ?? false);
  }
}
